import { useParams } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { ResourceManager } from "@/components/common/ResourceManager";
import { CancelarPagoButton } from "@/components/common/CancelarPagoButton";
import { CancelarGarantiaButton } from "@/components/common/CancelarGarantiaButton";
import { ReciboPagoButton } from "@/components/common/ReciboPagoButton";
import { ProspectoRiesgoPanel, ProspectoRiesgoButton } from "@/components/prospectos/ProspectoRiesgoPanel";
import { GeoVisitaProspecto } from "@/components/common/GeoVisitaProspecto";
import { SolicitudPldPanel } from "@/components/solicitudes/SolicitudPldPanel";
import { AutorizacionOcSolicitud } from "@/components/solicitudes/AutorizacionOcSolicitud";
import { IntegrantesGrupoPanel } from "@/components/solicitudes/IntegrantesGrupoPanel";
import { UbicacionGuardada } from "@/components/common/UbicacionGuardada";
import { MigracionAvisoCliente } from "@/components/migracion/MigracionAvisoCliente";
import { useAuth } from "@/context/AuthContext";
import { RESOURCES } from "@/lib/nav";

export default function GenericResourcePage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const esOC = user?.role === "oficial_cumplimiento";
  const config = RESOURCES[slug];
  // ago-2026 · Fase 1 Bloque 1 — `prospectos_av` es el esquema de Actividad Vulnerable
  // (mismo endpoint/colección `prospectos`), así que hereda los mismos extras de pantalla.
  const esProspectos = slug === "prospectos" || slug === "prospectos_av";

  if (!config)
    return (
      <div className="p-8">
        <PageHeader title="Módulo no encontrado" subtitle="Selecciona una opción válida del menú." />
      </div>
    );

  // En Pagos, el administrador de la entidad puede cancelar (lógicamente) un pago.
  // En Prospectos, cualquier rol con permiso de ver accede al "Riesgo PLD" (resultado del cotejo
  // + EBR). El ajuste excepcional dentro del panel queda restringido al Oficial de Cumplimiento.
  const extraRowActions =
    slug === "pagos"
      ? (row, { reload }) => (
          <span className="inline-flex items-center gap-1">
            <ReciboPagoButton pago={row} size="icon" />
            <CancelarPagoButton pago={row} onDone={reload} />
          </span>
        )
      : esProspectos
        ? (row, { reload }) => <ProspectoRiesgoButton prospecto={row} onDone={reload} />
        : slug === "garantias"
          ? (row, { reload }) => <CancelarGarantiaButton garantia={row} onDone={reload} />
          // oct-2026 · Solicitudes: etiqueta «Requiere autorización OC» en la lista (solo
          // para la Oficial de Cumplimiento, que tiene la solicitud en solo lectura). Al
          // pulsarla abre el EBR y su decisión única. Sin pantallas ni menús nuevos.
          : slug === "solicitudes"
            ? (row, { reload }) => (
                <AutorizacionOcSolicitud solicitud={row} onDone={reload} variant="row" />
              )
            : undefined;

  // En Prospectos, el resultado del cotejo PLD + EBR se muestra como salida de solo lectura
  // dentro del diálogo de edición, junto al ajuste excepcional del Oficial de Cumplimiento.
  // La geo-visita (dato de etapa temprana) también vive aquí tras retirar el modal de documentos.
  // En Solicitudes, se muestra el resultado PLD/EBR reutilizado del cliente (solo lectura).
  const extraEditContent =
    esProspectos
      ? (row, { reload }) => (
          <>
            <ProspectoRiesgoPanel prospecto={row} onDone={reload} />
            {row?.geo_lead?.lat != null && (
              <UbicacionGuardada geo={row.geo_lead} label="Ubicación del lead (domicilio del prospecto)" testid="prospecto-geo-lead" />
            )}
            <GeoVisitaProspecto recordId={row?.id} />
          </>
        )
      : slug === "solicitudes"
        // oct-2026 · El EBR (grado consolidado + 5 factores) en esta pantalla es insumo de la
        // decisión de cumplimiento: se muestra SOLO al Oficial de Cumplimiento. El bloque de
        // su decisión (autorizar con carta PDF / no autorizar) lo ven todos los roles una vez
        // tomada, para que el ejecutivo lea el motivo.
        ? (row, { reload }) => (
            <>
              <AutorizacionOcSolicitud solicitud={row} onDone={reload} />
              {esOC && <SolicitudPldPanel solicitud={row} />}
              <IntegrantesGrupoPanel solicitudId={row.id} onGuardado={reload} />
            </>
          )
        : slug === "clientes"
          // oct-2026 · Aviso de migración de cartera (cliente migrado o vinculado).
          ? (row) => <MigracionAvisoCliente cliente={row} />
          : undefined;

  return (
    <div>
      <PageHeader title={config.title} subtitle={config.subtitle} />
      <div className="p-6 md:p-8">
        <ResourceManager config={config} extraRowActions={extraRowActions} extraEditContent={extraEditContent} />
      </div>
    </div>
  );
}
